import React, { useState, useEffect } from 'react';
import PageFooter from '../components/PageFooter';
import ArtifactsViewer from '../components/ArtifactsViewer';
import LogViewer from '../components/LogViewer';
import AgentStatusWidget from '../components/AgentStatusWidget';
import workflowAPI from '../services/api';
import './Executions.css';

interface ExecutionStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'awaiting_approval';
  startTime?: string;
  endTime?: string;
  link?: string;
  approvalMessage?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
}

interface WorkflowExecution {
  id: string;
  workflowName: string;
  status: 'running' | 'completed' | 'failed' | 'pending' | 'pending_approval' | 'approved' | 'rejected' | 'terminated' | 'expired' |
          'sonar_analysis_complete' | 'jira_created' | 'waiting_for_implementation' | 'implementation_complete' | 'jira_updated';
  startTime: string;
  endTime?: string;
  duration?: string;
  triggeredBy: string;
  steps: ExecutionStep[];
  currentStep?: string;
  outputData?: any;
  error?: string;
  _lastUpdated?: number; // Timestamp to force React re-renders
}

// Build workflow steps based on execution status and workflow name
const buildWorkflowSteps = (status: string, startTime: string, workflowName?: string): ExecutionStep[] => {
  // Detect workflow type from workflow name
  const isCodeImplementation = workflowName?.toLowerCase().includes('code') ||
                                 workflowName?.toLowerCase().includes('implementation');
  const isSonarQube = workflowName?.toLowerCase().includes('sonar') ||
                      workflowName?.toLowerCase().includes('security');

  let steps: ExecutionStep[];

  if (isSonarQube) {
    // SonarQube Security Scan workflow steps (matches real template)
    steps = [
      {
        id: 'step-1',
        name: 'Run SonarQube Scan',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-2',
        name: 'Create JIRA Story',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-3',
        name: 'Wait for Implementation',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-4',
        name: 'PR Approval',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-5',
        name: 'Update JIRA',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      }
    ];
  } else if (isCodeImplementation) {
    // Code Implementation workflow steps
    steps = [
      {
        id: 'step-1',
        name: 'Implement User Story',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-2',
        name: 'Write Unit Tests',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-3',
        name: 'Create Pull Request',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-4',
        name: 'PR Approval',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-5',
        name: 'Update JIRA',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      }
    ];
  } else {
    // Default Epic workflow steps
    steps = [
      {
        id: 'step-1',
        name: 'Epic & Story Generation',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      },
      {
        id: 'step-2',
        name: 'Manual Approval',
        status: 'pending',
        startTime: undefined,
        endTime: undefined,
        approvalMessage: 'Please review and approve the generated epic and stories',
        approvalStatus: 'pending'
      },
      {
        id: 'step-3',
        name: 'Create JIRA Tickets',
        status: 'pending',
        startTime: undefined,
        endTime: undefined
      }
    ];
  }

  // Update step statuses based on workflow status
  if (isSonarQube) {
    // SonarQube workflow - 5 steps: Scan → JIRA → Wait → Approve → Update
    if (status === 'running') {
      // Step 1: SonarQube Scan is running
      steps[0].status = 'running';
      steps[0].startTime = startTime;
    } else if (status === 'sonar_analysis_complete' || status === 'jira_created') {
      // Step 1 complete, Step 2 running or complete
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = status === 'jira_created' ? 'completed' : 'running';
      steps[1].startTime = new Date().toISOString();
      if (status === 'jira_created') {
        steps[1].endTime = new Date().toISOString();
      }
    } else if (status === 'waiting_for_implementation') {
      // Steps 1-2 complete, Step 3 (Wait for Implementation) running
      steps[0].status = 'completed';
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].endTime = new Date().toISOString();
      steps[2].status = 'running';
      steps[2].startTime = new Date().toISOString();
    } else if (status === 'implementation_complete') {
      // Steps 1-3 complete
      steps[0].status = 'completed';
      steps[1].status = 'completed';
      steps[2].status = 'completed';
      steps[2].endTime = new Date().toISOString();
    } else if (status === 'pending_approval') {
      // Steps 1-3 complete, Step 4 (PR Approval) running
      steps[0].status = 'completed';
      steps[1].status = 'completed';
      steps[2].status = 'completed';
      steps[3].status = 'running';
      steps[3].startTime = new Date().toISOString();
    } else if (status === 'approved' || status === 'jira_updated') {
      // Steps 1-4 complete, Step 5 (Update JIRA) running or complete
      steps[0].status = 'completed';
      steps[1].status = 'completed';
      steps[2].status = 'completed';
      steps[3].status = 'completed';
      steps[3].endTime = new Date().toISOString();
      steps[4].status = status === 'jira_updated' ? 'completed' : 'running';
      steps[4].startTime = new Date().toISOString();
      if (status === 'jira_updated') {
        steps[4].endTime = new Date().toISOString();
      }
    } else if (status === 'completed') {
      // All steps completed
      steps.forEach((step, index) => {
        step.status = 'completed';
        step.startTime = startTime;
        step.endTime = new Date().toISOString();
      });
    } else if (status === 'failed') {
      steps[0].status = 'failed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
    }
  } else if (isCodeImplementation) {
    // Code implementation workflow - HAS approval step (Step 4: PR Approval)
    if (status === 'running') {
      // Steps 1-3 running (Implementation, Tests, Create PR)
      steps[0].status = 'running';
      steps[0].startTime = startTime;
    } else if (status === 'pending_approval') {
      // Steps 1-3 completed, Step 4 (PR Approval) is now pending
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].startTime = startTime;
      steps[1].endTime = new Date().toISOString();
      steps[2].status = 'completed';
      steps[2].startTime = startTime;
      steps[2].endTime = new Date().toISOString();
      steps[3].status = 'running'; // PR Approval step
      steps[3].startTime = new Date().toISOString();
      steps[3].approvalMessage = 'Review the Pull Request before updating JIRA';
      steps[3].approvalStatus = 'pending';
    } else if (status === 'approved') {
      // Steps 1-4 completed, Step 5 (Update JIRA) is running
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].startTime = startTime;
      steps[1].endTime = new Date().toISOString();
      steps[2].status = 'completed';
      steps[2].startTime = startTime;
      steps[2].endTime = new Date().toISOString();
      steps[3].status = 'completed'; // PR Approval approved
      steps[3].startTime = new Date().toISOString();
      steps[3].endTime = new Date().toISOString();
      steps[3].approvalMessage = 'Review the Pull Request before updating JIRA';
      steps[3].approvalStatus = 'approved';
      steps[4].status = 'running'; // Update JIRA
      steps[4].startTime = new Date().toISOString();
    } else if (status === 'completed') {
      // All steps completed
      steps.forEach((step, index) => {
        step.status = 'completed';
        step.startTime = startTime;
        step.endTime = new Date().toISOString();
      });
      steps[3].approvalMessage = 'Review the Pull Request before updating JIRA';
      steps[3].approvalStatus = 'approved';
    } else if (status === 'rejected' || status === 'terminated') {
      // Rejected at approval step
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].startTime = startTime;
      steps[1].endTime = new Date().toISOString();
      steps[2].status = 'completed';
      steps[2].startTime = startTime;
      steps[2].endTime = new Date().toISOString();
      steps[3].status = 'failed'; // PR Approval rejected
      steps[3].startTime = new Date().toISOString();
      steps[3].endTime = new Date().toISOString();
      steps[3].approvalMessage = 'Review the Pull Request before updating JIRA';
      steps[3].approvalStatus = 'rejected';
      steps[4].status = 'pending';
    } else if (status === 'failed') {
      steps[0].status = 'failed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
    }
  } else {
    // Epic workflow - has approval step
    if (status === 'running') {
      steps[0].status = 'running';
      steps[0].startTime = startTime;
    } else if (status === 'pending_approval') {
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'running';
      steps[1].startTime = new Date().toISOString();
      if (steps[1].approvalStatus) {
        steps[1].approvalStatus = 'pending';
      }
    } else if (status === 'approved') {
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].startTime = new Date().toISOString();
      steps[1].endTime = new Date().toISOString();
      if (steps[1].approvalStatus) {
        steps[1].approvalStatus = 'approved';
      }
      steps[2].status = 'running';
      steps[2].startTime = new Date().toISOString();
    } else if (status === 'completed') {
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'completed';
      steps[1].startTime = new Date().toISOString();
      steps[1].endTime = new Date().toISOString();
      if (steps[1].approvalStatus) {
        steps[1].approvalStatus = 'approved';
      }
      steps[2].status = 'completed';
      steps[2].startTime = new Date().toISOString();
      steps[2].endTime = new Date().toISOString();
    } else if (status === 'rejected' || status === 'terminated') {
      steps[0].status = 'completed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
      steps[1].status = 'failed';
      steps[1].startTime = new Date().toISOString();
      steps[1].endTime = new Date().toISOString();
      if (steps[1].approvalStatus) {
        steps[1].approvalStatus = 'rejected';
      }
      steps[2].status = 'pending';
    } else if (status === 'failed') {
      steps[0].status = 'failed';
      steps[0].startTime = startTime;
      steps[0].endTime = new Date().toISOString();
    }
  }

  console.log(`buildWorkflowSteps: status=${status}, steps count=${steps.length}`);
  return steps;
};

const Executions: React.FC = () => {
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<WorkflowExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [renderKey, setRenderKey] = useState(0); // Force re-render when needed
  const selectedExecutionRef = React.useRef<WorkflowExecution | null>(null);
  const previousStatusRef = React.useRef<string | null>(null);
  const hasAutoSelectedRef = React.useRef<boolean>(false); // Track if we've auto-selected

  // Fetch executions from API
  const fetchExecutions = async () => {
    try {
      console.log('Fetching executions...');
      const data = await workflowAPI.getExecutions();
      console.log('Raw execution data from API:', data);

      if (!Array.isArray(data)) {
        console.error('Expected array but got:', typeof data, data);
        throw new Error('Invalid response format from API');
      }

      // Transform backend data to match UI format
      const transformedExecutions = data.map((exec: any) => {
        console.log('Processing execution:', exec);
        // Ensure triggeredBy is always a string
        let triggeredBy = 'system';
        if (exec.triggered_by) {
          if (typeof exec.triggered_by === 'string') {
            triggeredBy = exec.triggered_by;
          } else if (typeof exec.triggered_by === 'object') {
            triggeredBy = JSON.stringify(exec.triggered_by);
          }
        }

        // Ensure status is one of the valid values
        const validStatuses = ['running', 'completed', 'failed', 'pending', 'pending_approval', 'approved', 'rejected', 'terminated', 'expired',
                               'sonar_analysis_complete', 'jira_created', 'waiting_for_implementation', 'implementation_complete', 'jira_updated'];
        const status = validStatuses.includes(exec.status) ? exec.status : 'pending';

        // Use real steps from backend if available, otherwise build fake steps
        const workflowName = exec.workflow_name || 'Unknown Workflow';
        let steps: ExecutionStep[];

        if (exec.steps && Array.isArray(exec.steps) && exec.steps.length > 0) {
          // Use real steps from backend
          console.log(`✅ Using REAL steps from backend for execution ${exec.id}:`, exec.steps);
          steps = exec.steps.map((step: any) => ({
            id: step.id,
            name: step.name,
            status: step.status,
            startTime: step.startedAt,
            endTime: step.completedAt
          }));
        } else {
          // Fallback to building fake steps (for backwards compatibility)
          console.log(`⚠️ No steps from backend, building fake steps for ${exec.id}`);
          steps = buildWorkflowSteps(exec.status, exec.started_at, workflowName);
        }

        // Extract outputData from stepData.outputData (progressive output from Temporal query)
        // Fallback to exec.output_data for backwards compatibility
        const outputData = exec.stepData?.outputData || exec.output_data;

        return {
          id: exec.id,
          workflowName: exec.workflow_name || 'Unknown Workflow',
          status: status as WorkflowExecution['status'],
          startTime: exec.started_at,
          endTime: exec.completed_at,
          duration: exec.completed_at ? calculateDuration(exec.started_at, exec.completed_at) : undefined,
          triggeredBy,
          steps,
          currentStep: exec.current_step,
          outputData: outputData,
          error: exec.error
        };
      });

      // Sort executions by start time (latest first)
      const sortedExecutions = transformedExecutions.sort((a, b) => {
        return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
      });

      console.log('Transformed and sorted executions:', sortedExecutions);
      setExecutions(sortedExecutions);

      // Auto-select the most recent execution ONLY ONCE if it's in pending_approval, completed, or waiting_for_implementation status
      // This ensures buttons/JIRA links/progressive output appear automatically without clicking
      // But doesn't interfere with manual selection
      if (!selectedExecution && !hasAutoSelectedRef.current && sortedExecutions.length > 0) {
        const mostRecent = sortedExecutions[0]; // Already sorted by start time descending
        // Auto-select for any status that should show progressive output
        const shouldAutoSelect = mostRecent.status === 'pending_approval' ||
                                  mostRecent.status === 'completed' ||
                                  mostRecent.status === 'waiting_for_implementation' ||
                                  mostRecent.status === 'jira_created' ||
                                  (mostRecent.outputData && Object.keys(mostRecent.outputData).length > 0);

        if (shouldAutoSelect) {
          console.log(`🎯 AUTO-SELECTING execution ${mostRecent.id} with status ${mostRecent.status} (has output: ${!!mostRecent.outputData})`);
          const autoSelected = {
            ...mostRecent,
            _lastUpdated: Date.now()
          } as any;
          setSelectedExecution(autoSelected);
          selectedExecutionRef.current = autoSelected;
          setRenderKey(prev => prev + 1);
          hasAutoSelectedRef.current = true; // Mark that we've auto-selected
        }
      }

      // Update selectedExecution if it exists in the new list
      if (selectedExecution) {
        const updatedExecution = sortedExecutions.find(exec => exec.id === selectedExecution.id);
        if (updatedExecution) {
          console.log('🔄 Updating selectedExecution:', {
            from: { status: selectedExecution.status, step: selectedExecution.currentStep },
            to: { status: updatedExecution.status, step: updatedExecution.currentStep },
            changed: selectedExecution.status !== updatedExecution.status || selectedExecution.currentStep !== updatedExecution.currentStep
          });
          // Always update to force re-render, even if values are the same
          // Add a timestamp to ensure React sees this as a new object
          const newSelectedExecution = {
            ...updatedExecution,
            _lastUpdated: Date.now()
          } as any;

          // Track status change
          const oldStatus = selectedExecutionRef.current?.status;
          const newStatus = newSelectedExecution.status;
          const oldOutputData = selectedExecutionRef.current?.outputData;
          const newOutputData = newSelectedExecution.outputData;

          // Check if outputData changed (for JIRA links, SonarQube links, PR links)
          // Compare JSON stringification to detect any changes in the outputData object
          const oldOutputDataStr = oldOutputData ? JSON.stringify(oldOutputData) : '{}';
          const newOutputDataStr = newOutputData ? JSON.stringify(newOutputData) : '{}';
          const outputDataChanged = oldOutputDataStr !== newOutputDataStr && newOutputDataStr !== '{}';

          // Force re-render on important changes
          let shouldForceRender = false;

          if (oldStatus !== newStatus) {
            console.log(`🔄 Status changed: ${oldStatus} → ${newStatus}`);
            // Force re-render for critical status changes (including waiting states with progressive output)
            const criticalStatuses = ['pending_approval', 'completed', 'waiting_for_implementation', 'jira_created', 'sonar_analysis_complete'];
            if (criticalStatuses.includes(newStatus)) {
              console.log(`🎯 Critical status change detected, forcing re-render`);
              shouldForceRender = true;
            }
          }

          if (outputDataChanged) {
            console.log(`🎫 OutputData changed:`, {
              from: oldOutputDataStr,
              to: newOutputDataStr
            });
            shouldForceRender = true;
          }

          // Force re-render if needed
          if (shouldForceRender) {
            setRenderKey(prev => prev + 1);
          }

          // Always update the selected execution with new data
          // The _lastUpdated timestamp ensures React sees this as a new object
          setSelectedExecution(newSelectedExecution);
          selectedExecutionRef.current = newSelectedExecution;
          previousStatusRef.current = newStatus;
        }
      }

      setLoading(false);
      console.log('Loading set to false');
    } catch (err: any) {
      console.error('Error fetching executions:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const calculateDuration = (start: string, end: string) => {
    const diff = new Date(end).getTime() - new Date(start).getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  useEffect(() => {
    console.log('🔄 Initial fetch of executions');
    fetchExecutions();

    // Auto-refresh every 1 second for faster UI updates
    const interval = setInterval(() => {
      const currentStatus = selectedExecutionRef.current?.status;

      // Continue polling for all statuses to ensure UI stays in sync
      // This ensures Approve/Reject buttons appear immediately when status changes to pending_approval
      console.log('🔄 Polling for execution updates... Current status:', currentStatus);
      fetchExecutions();
    }, 1000); // Poll every 1 second for faster status updates

    return () => {
      console.log('🛑 Stopping execution polling');
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Log when selectedExecution changes
  useEffect(() => {
    if (selectedExecution) {
      console.log('📊 Selected execution updated:', {
        id: selectedExecution.id,
        status: selectedExecution.status,
        currentStep: selectedExecution.currentStep,
        triggeredBy: selectedExecution.triggeredBy,
        triggeredByType: typeof selectedExecution.triggeredBy,
        hasOutputData: !!selectedExecution.outputData,
        outputData: selectedExecution.outputData,
        shouldShowArtifacts: selectedExecution.status === 'pending_approval',
        shouldShowJira: selectedExecution.status === 'completed' && !!selectedExecution.outputData?.epicTicket
      });
    }
  }, [selectedExecution]);

  const handleApprove = async (artifacts: any) => {
    if (!selectedExecution) return;

    try {
      await workflowAPI.approveExecution(selectedExecution.id, artifacts);
      alert('✅ Execution approved! JIRA tickets will be created.');
      fetchExecutions();
    } catch (err: any) {
      alert(`Error approving execution: ${err.message}`);
    }
  };

  const handleTerminate = async () => {
    if (!selectedExecution) return;

    const confirmed = window.confirm(
      `Are you sure you want to terminate the workflow "${selectedExecution.workflowName}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await workflowAPI.terminateExecution(selectedExecution.id);
      alert('✅ Workflow terminated successfully.');
      fetchExecutions();
      setSelectedExecution(null);
      selectedExecutionRef.current = null;
    } catch (err: any) {
      alert(`Error terminating workflow: ${err.message}`);
    }
  };

  const handleReject = async (reason?: string) => {
    if (!selectedExecution) return;

    try {
      await workflowAPI.rejectExecution(selectedExecution.id, reason);
      alert('❌ Execution rejected.');
      fetchExecutions();
    } catch (err: any) {
      alert(`Error rejecting execution: ${err.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'running': return '#007bff';
      case 'failed': return '#dc3545';
      case 'pending': return '#6c757d';
      case 'pending_approval': return '#ffc107';
      case 'awaiting_approval': return '#ffc107';
      case 'terminated': return '#dc3545';
      case 'rejected': return '#dc3545';
      case 'expired': return '#ff8c00'; // Orange for expired
      // SonarQube workflow specific statuses
      case 'sonar_analysis_complete': return '#17a2b8'; // Cyan - analysis done
      case 'jira_created': return '#28a745'; // Green - JIRA created
      case 'waiting_for_implementation': return '#fd7e14'; // Orange - waiting for external agent
      case 'implementation_complete': return '#20c997'; // Teal - PR created
      case 'jira_updated': return '#6f42c1'; // Purple - final update
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'sonar_analysis_complete': return 'Sonar Analysis Complete';
      case 'jira_created': return 'JIRA Created';
      case 'waiting_for_implementation': return 'Waiting for Implementation';
      case 'implementation_complete': return 'Implementation Complete (PR Created)';
      case 'jira_updated': return 'JIRA Updated';
      case 'pending_approval': return 'Pending Approval';
      default: return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="executions-page">
      <div className="executions-header">
        <h1>Workflow Executions</h1>
        <p>Monitor and track workflow execution history</p>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading executions...</p>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
          <p>Error: {error}</p>
          <button onClick={fetchExecutions} style={{ marginTop: '10px', padding: '8px 16px' }}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && executions.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No executions found. Execute a workflow to see it here.</p>
        </div>
      )}

      {!loading && !error && executions.length > 0 && (
        <div className="executions-content">
          <div className="executions-list">
            {executions.map(execution => (
            <div
              key={execution.id}
              className={`execution-card ${selectedExecution?.id === execution.id ? 'selected' : ''}`}
              onClick={() => {
                console.log(`🖱️ Clicked execution: ${execution.id}, status: ${execution.status}`);
                setSelectedExecution(execution);
                selectedExecutionRef.current = execution;
                // Force re-render to ensure UI components appear immediately
                setRenderKey(prev => prev + 1);
              }}
            >
              <div className="execution-header">
                <h3>{execution.workflowName}</h3>
                <span className="status-badge" style={{ background: getStatusColor(execution.status) }}>
                  {execution.status}
                </span>
              </div>
              <div className="execution-meta">
                <span>ID: {execution.id}</span>
                <span>By: {String(execution.triggeredBy || 'system')}</span>
                <span>{new Date(execution.startTime).toLocaleString()}</span>
                {execution.duration && <span>Duration: {execution.duration}</span>}
              </div>
              <div className="execution-progress">
                <div className="progress-bar">
                  {Array.isArray(execution.steps) && execution.steps.map((step, idx) => (
                    <div
                      key={step.id}
                      className="progress-segment"
                      style={{
                        width: `${100 / execution.steps.length}%`,
                        background: getStatusColor(step.status)
                      }}
                      title={step.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedExecution && (
          <div className="execution-details">
            <h2>Execution Details</h2>
            <div className="details-info">
              <p><strong>Workflow:</strong> {selectedExecution.workflowName}</p>
              <p><strong>Status:</strong> <span style={{ color: getStatusColor(selectedExecution.status), fontWeight: 'bold' }}>{getStatusLabel(selectedExecution.status)}</span></p>
              <p><strong>Started:</strong> {new Date(selectedExecution.startTime).toLocaleString()}</p>
              {selectedExecution.endTime && <p><strong>Ended:</strong> {new Date(selectedExecution.endTime).toLocaleString()}</p>}
              {selectedExecution.duration && <p><strong>Duration:</strong> {selectedExecution.duration}</p>}
              <p><strong>Triggered By:</strong> {String(selectedExecution.triggeredBy || 'system')}</p>
            </div>

            {/* Terminate button for running workflows */}
            {(selectedExecution.status === 'running' || selectedExecution.status === 'pending_approval') && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <button
                  onClick={handleTerminate}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                >
                  🛑 Terminate Workflow
                </button>
              </div>
            )}

            {/* Delete button for expired workflows */}
            {selectedExecution.status === 'expired' && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffc107',
                  marginBottom: '1rem'
                }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#856404', fontWeight: 'bold' }}>⏰ Workflow Expired</p>
                  <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
                    {selectedExecution.error || 'This workflow has expired and can no longer be resumed.'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to delete this expired workflow? This will permanently delete all artifacts and data. This action cannot be undone.')) {
                      try {
                        await workflowAPI.deleteExecution(selectedExecution.id);
                        alert('✅ Expired workflow deleted successfully.');
                        fetchExecutions();
                        setSelectedExecution(null);
                      } catch (err: any) {
                        alert(`Error deleting execution: ${err.message}`);
                      }
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                >
                  🗑️ Delete Expired Workflow
                </button>
              </div>
            )}

            <h3>Steps</h3>
            <div className="steps-timeline">
              {Array.isArray(selectedExecution.steps) && selectedExecution.steps.map((step, idx) => (
                <div key={step.id} className="timeline-item">
                  <div className="timeline-marker" style={{ background: getStatusColor(step.status) }}>
                    {idx + 1}
                  </div>
                  <div className="timeline-content">
                    <h4>{step.name}</h4>
                    <p className="step-status" style={{ color: getStatusColor(step.status) }}>{step.status}</p>
                    {step.startTime && <p className="step-time">Started: {new Date(step.startTime).toLocaleString()}</p>}
                    {step.endTime && <p className="step-time">Ended: {new Date(step.endTime).toLocaleString()}</p>}

                    {step.link && (
                      <div className="step-link">
                        <a href={step.link} target="_blank" rel="noopener noreferrer" className="artifact-link">
                          🔗 View Artifact
                        </a>
                      </div>
                    )}

                    {step.approvalMessage && (
                      <div className="approval-section">
                        <p className="approval-message">
                          {step.approvalMessage}
                        </p>
                        {step.approvalStatus && (
                          <p className={`approval-status ${step.approvalStatus}`}>
                            {step.approvalStatus === 'approved' && '✓ Approved'}
                            {step.approvalStatus === 'rejected' && '✗ Rejected'}
                            {step.approvalStatus === 'pending' && '⏳ Pending Approval'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Autonomous Agent Status Widget - only for SonarQube/Security workflows */}
            <AgentStatusWidget
              executionId={selectedExecution.id}
              jiraTicket={selectedExecution.outputData?.jiraTicketId}
              workflowName={selectedExecution.workflowName}
            />

            {/* Real-time log viewer - shows logs for all steps */}
            <LogViewer
              executionId={selectedExecution.id}
              isPendingApproval={selectedExecution.status === 'pending_approval'}
            />

            {/* Show artifacts viewer when status is pending_approval OR completed (for PR workflows) */}
            {(selectedExecution.status === 'pending_approval' ||
              (selectedExecution.status === 'completed' && selectedExecution.workflowName.toLowerCase().includes('code'))) && (
              <ArtifactsViewer
                key={`artifacts-${selectedExecution.id}-${renderKey}`}
                executionId={selectedExecution.id}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            )}

            {/* Show JIRA epic link as artifact when EPIC workflow is completed successfully */}
            {selectedExecution.status === 'completed' &&
             selectedExecution.outputData &&
             selectedExecution.outputData.epicTicket &&
             !selectedExecution.workflowName.toLowerCase().includes('code') && (
              <div key={`jira-epic-${selectedExecution.id}-${renderKey}`} className="jira-tickets-result" style={{
                marginTop: '2rem',
                padding: '1.5rem',
                backgroundColor: '#d4edda',
                borderRadius: '8px',
                border: '1px solid #c3e6cb'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#155724' }}>✅ JIRA Tickets Created Successfully!</h3>

                {/* Epic Ticket */}
                <div style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                  <strong>Epic:</strong>{' '}
                  <a
                    href={selectedExecution.outputData.epicTicket.url || `https://your-jira-instance.atlassian.net/browse/${selectedExecution.outputData.epicTicket.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#007bff',
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    {selectedExecution.outputData.epicTicket.url || `https://your-jira-instance.atlassian.net/browse/${selectedExecution.outputData.epicTicket.key}`}
                  </a>
                  {selectedExecution.outputData.epicTicket.title && (
                    <div style={{ marginTop: '0.25rem', color: '#666', fontSize: '0.95rem' }}>
                      {selectedExecution.outputData.epicTicket.title}
                    </div>
                  )}
                </div>

                {/* Story Tickets */}
                {selectedExecution.outputData.storyTickets && selectedExecution.outputData.storyTickets.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Stories ({selectedExecution.outputData.storyTickets.length}):</strong>
                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                      {selectedExecution.outputData.storyTickets.map((story: any, index: number) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>
                          <a
                            href={story.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#007bff',
                              textDecoration: 'underline'
                            }}
                          >
                            {story.url}
                          </a>
                          {story.title && (
                            <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                              - {story.title}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Show SonarQube workflow outputs (running or completed) */}
            {selectedExecution.workflowName.toLowerCase().includes('sonar') &&
             selectedExecution.outputData &&
             Object.keys(selectedExecution.outputData).length > 0 && (
              <div key={`sonar-output-${selectedExecution.id}-${renderKey}`} style={{
                marginTop: '2rem',
                padding: '1.5rem',
                backgroundColor: '#e7f3ff',
                borderRadius: '8px',
                border: '1px solid #b3d9ff'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#004085' }}>
                  📊 SonarQube Security Scan Results
                </h3>

                {/* SonarQube Scan Results */}
                {selectedExecution.outputData.sonarScan && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                      <strong>Scan Status:</strong>{' '}
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>✅ Completed</span>
                    </div>

                    {selectedExecution.outputData.sonarScan.sonarUrl && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <strong>SonarQube Dashboard:</strong>{' '}
                        <a
                          href={selectedExecution.outputData.sonarScan.sonarUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007bff',
                            textDecoration: 'underline',
                            fontWeight: 'bold'
                          }}
                        >
                          {selectedExecution.outputData.sonarScan.sonarUrl}
                        </a>
                      </div>
                    )}

                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <strong>Issues Found:</strong>
                      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
                        <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                          🔴 Critical: {selectedExecution.outputData.sonarScan.criticalCount || 0}
                        </span>
                        <span style={{ color: '#fd7e14', fontWeight: 'bold' }}>
                          🟠 High: {selectedExecution.outputData.sonarScan.highCount || 0}
                        </span>
                        <span style={{ color: '#6c757d' }}>
                          Total: {selectedExecution.outputData.sonarScan.totalIssues || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* JIRA Story Results */}
                {selectedExecution.outputData.jiraStory && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #b3d9ff' }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                      <strong>JIRA Ticket:</strong>{' '}
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>✅ Created</span>
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <a
                        href={selectedExecution.outputData.jiraStory.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}
                      >
                        {selectedExecution.outputData.jiraStory.ticketId || selectedExecution.outputData.jiraStory.ticketUrl}
                      </a>
                    </div>

                    {selectedExecution.outputData.jiraStory.ticketTitle && (
                      <div style={{ marginTop: '0.25rem', color: '#666', fontSize: '0.95rem' }}>
                        {selectedExecution.outputData.jiraStory.ticketTitle}
                      </div>
                    )}

                    {selectedExecution.outputData.jiraStory.priority && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <strong>Priority:</strong> <span style={{
                          color: selectedExecution.outputData.jiraStory.priority === 'Critical' ? '#dc3545' : '#fd7e14',
                          fontWeight: 'bold'
                        }}>
                          {selectedExecution.outputData.jiraStory.priority}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* PR Results (for future implementation step) */}
                {selectedExecution.outputData.pullRequest && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #b3d9ff' }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                      <strong>Pull Request:</strong>{' '}
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>✅ Created</span>
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <a
                        href={selectedExecution.outputData.pullRequest.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}
                      >
                        PR #{selectedExecution.outputData.pullRequest.prNumber}: {selectedExecution.outputData.pullRequest.prTitle || 'View Pull Request'}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show message when workflow failed (including JIRA creation failures) */}
            {selectedExecution.status === 'failed' && (
              <div className="jira-tickets-result" style={{
                marginTop: '2rem',
                padding: '1.5rem',
                backgroundColor: '#f8d7da',
                borderRadius: '8px',
                border: '1px solid #f5c6cb'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#721c24' }}>❌ Workflow Failed</h3>
                <p style={{ color: '#721c24', marginBottom: '0.5rem' }}>
                  The workflow execution failed. Check the logs above for error details.
                </p>
                {selectedExecution.currentStep === 'create-jira-tickets' && (
                  <p style={{ color: '#721c24', fontSize: '0.9rem' }}>
                    The epic and stories artifacts are still available for manual JIRA creation.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      )}
      <PageFooter />
    </div>
  );
};

export default Executions;

